'use client';
import React, { useMemo, useState, FormEvent, ChangeEvent } from 'react';

type Audience = 'employers' | 'seekers' | 'corporate';
type Kind = 'article' | 'checklist' | 'template' | 'case' | 'webinar';

type Resource = {
  id: number;
  title: string;
  desc: string;
  audience: Audience;
  type: Kind;
  date: string;
  read: number;
  href: string;
};

const DATA: Resource[] = [
  { id: 1, title: 'Как составить JD, который продаёт вакансию', desc: 'Структура, примеры, типичные ошибки.', audience: 'employers', type: 'article', date: '2025-07-20', read: 7, href: '#' },
  { id: 2, title: 'Чек-лист запуска найма в новом регионе', desc: 'Каналы, бюджет, KPI и локальные риски.', audience: 'corporate', type: 'checklist', date: '2025-06-28', read: 5, href: '#' },
  { id: 3, title: 'Шаблон резюме под ATS', desc: 'Готовый .docx + инструкция по ключевым словам.', audience: 'seekers', type: 'template', date: '2025-08-01', read: 3, href: '#' },
  { id: 4, title: 'Кейс: 48 наймов за 6 недель', desc: 'Склад и логистика, явка 97%, SLA 24ч.', audience: 'employers', type: 'case', date: '2025-05-12', read: 4, href: '#' },
  { id: 5, title: 'Запись вебинара: MSP/вендор-менеджмент', desc: 'Единые SLA, тарификация и отчётность.', audience: 'corporate', type: 'webinar', date: '2025-08-10', read: 45, href: '#' },
  { id: 6, title: 'Гид по собеседованию для соискателя', desc: 'STAR-ответы, кейсы, вопросы о зарплате.', audience: 'seekers', type: 'article', date: '2025-07-10', read: 10, href: '#' },
  { id: 7, title: 'Шаблон KPI рекрутера/RPO', desc: 'Набор метрик с формулами.', audience: 'corporate', type: 'template', date: '2025-07-30', read: 6, href: '#' },
  { id: 8, title: 'Чек-лист онбординга (первые 30 дней)', desc: 'Цели, наставник, культура и процессы.', audience: 'employers', type: 'checklist', date: '2025-06-10', read: 5, href: '#' },
  { id: 9, title: 'Запись вебинара: Резюме, которое проходит ATS', desc: 'Практика и разбор примеров.', audience: 'seekers', type: 'webinar', date: '2025-08-05', read: 50, href: '#' },
];

export default function ResourcesPage() {
  const [q, setQ] = useState('');
  const [aud, setAud] = useState<'' | Audience>('');
  const [kind, setKind] = useState<'' | Kind>('');
  const [sort, setSort] = useState<'new' | 'old' | 'alpha'>('new');

  const [suggestionForm, setSuggestionForm] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [isAgreed, setIsAgreed] = useState(false);
  const [suggestionError, setSuggestionError] = useState<string | null>(null);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [subscribeForm, setSubscribeForm] = useState({
    name: '',
    email: '',
  });
  const [subscribeStatus, setSubscribeStatus] = useState<'success' | 'error' | null>(null);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const handleSuggestionChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setSuggestionForm({ ...suggestionForm, [name]: value });
  };

  const handleAgreementChange = (e: ChangeEvent<HTMLInputElement>) => {
    setIsAgreed(e.target.checked);
    setSuggestionError(null);
  };

  const handleSubscribeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSubscribeForm({ ...subscribeForm, [name]: value });
  };

  const handleSuggestionSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSuggestionError(null);
    setSubmitStatus(null);

    if (!isAgreed) {
      setSuggestionError('Пожалуйста, дайте согласие на обработку персональных данных.');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_STRAPI_API_URL}/api/resource-suggestions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: {
            ...suggestionForm,
            formName: "Ресурсы"
          }
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setSuggestionForm({ name: '', email: '', message: '' });
        setIsAgreed(false);
      } else {
        setSubmitStatus('error');
        console.error('Strapi response error for suggestion:', response.status, await response.text());
      }
    } catch (error) {
      console.error('Error submitting suggestion form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubscribeSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubscribing(true);
    setSubscribeStatus(null);

    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_STRAPI_API_URL}/api/newsletter-subscribers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: {
            ...subscribeForm,
            formName: "Ресурсы/Рассылка"
          }
        }),
      });

      if (response.ok) {
        setSubscribeStatus('success');
        setSubscribeForm({ name: '', email: '' });
      } else {
        setSubscribeStatus('error');
        console.error('Strapi response error for subscribe:', response.status, await response.text());
      }
    } catch (error) {
      console.error('Error submitting subscribe form:', error);
      setSubscribeStatus('error');
    } finally {
      setIsSubscribing(false);
    }
  };

  const items = useMemo(() => {
    const ql = q.trim().toLowerCase();
    let arr = DATA.filter(r => {
      const okQ = !ql || r.title.toLowerCase().includes(ql) || r.desc.toLowerCase().includes(ql);
      const okA = !aud || r.audience === aud;
      const okT = !kind || r.type === kind;
      return okQ && okA && okT;
    });
    arr.sort((a, b) => {
      if (sort === 'alpha') return a.title.localeCompare(b.title);
      if (sort === 'old') return +new Date(a.date) - +new Date(b.date);
      return +new Date(b.date) - +new Date(a.date);
    });
    return arr;
  }, [q, aud, kind, sort]);

  const badge = (a: Audience) => {
    const cls = a === 'employers' ? 'bg-yellow-400/30 text-yellow-950'
      : a === 'seekers' ? 'bg-green-400/30 text-green-950'
        : 'bg-cyan-400/30 text-cyan-950';
    const text = a === 'employers' ? 'Работодателям' : a === 'seekers' ? 'Соискателям' : 'Партнёрам';
    return <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${cls}`}>{text}</span>;
  };

  return (
    <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-gray-100">
      <header className="px-6 pt-32 pb-16 md:pt-40 md:pb-20"
        style={{ background: 'linear-gradient(rgba(0,0,0,.45), rgba(0,0,0,.25)), radial-gradient(1200px 600px at 70% -10%, rgba(255,255,255,.08), transparent)' }}>
        <div className="max-w-7xl mx-auto">
          <nav className="mb-6 flex flex-wrap gap-3 text-sm">
            {['#hub', 'Все материалы', '#templates', 'Шаблоны', '#webinars', 'Вебинары', '#cases', 'Кейсы', '#subscribe', 'Рассылка'].reduce<any[]>((acc, cur, i, arr) => {
              if (i % 2 === 0) acc.push(<a key={cur} href={cur} className="px-3 py-1 rounded-full hover:bg-white/25" style={{ background: 'rgba(255,255,255,.14)' }}>{arr[i + 1]}</a>);
              return acc;
            }, [])}
          </nav>
          <h1 className="text-4xl md:text-6xl font-extrabold mb-4">Ресурсы для роста и найма</h1>
          <p className="max-w-3xl text-lg md:text-xl text-gray-200">
            Статьи, чек-листы, шаблоны, кейсы и записи вебинаров — всё, чтобы нанимать быстрее и развивать карьеру.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a href="#hub" className="px-4 py-3 rounded-xl font-semibold" style={{ background: 'rgba(255,255,255,.1)' }}>Смотреть материалы</a>
            <a href="#submit" className="px-4 py-3 rounded-xl font-semibold" style={{ background: '#FFC300', color: '#102a43' }}>Предложить тему</a>
          </div>
        </div>
      </header>

      <main className="px-6 py-12 md:py-16 max-w-7xl mx-auto space-y-14">
        <section id="hub" className="space-y-5 scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold">Медиа-хаб</h2>
          <div className="grid gap-3 md:grid-cols-4">
            <div className="md:col-span-2">
              <input value={q} onChange={e => setQ(e.target.value)}
                className="w-full rounded-xl px-4 py-3 text-gray-900" placeholder="Поиск по материалам…" />
            </div>
            <div>
              <select value={aud} onChange={e => setAud(e.target.value as any)}
                className="w-full rounded-xl px-4 py-3 text-gray-900">
                <option value="">Аудитория: все</option>
                <option value="employers">Работодатели</option>
                <option value="seekers">Соискатели</option>
                <option value="corporate">Корпоративные партнёры</option>
              </select>
            </div>
            <div>
              <select value={kind} onChange={e => setKind(e.target.value as any)}
                className="w-full rounded-xl px-4 py-3 text-gray-900">
                <option value="">Тип: все</option>
                <option value="article">Статьи</option>
                <option value="checklist">Чек-листы</option>
                <option value="template">Шаблоны</option>
                <option value="case">Кейсы</option>
                <option value="webinar">Вебинары</option>
              </select>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-300">Сортировка:</span>
            <select value={sort} onChange={e => setSort(e.target.value as any)}
              className="rounded-lg px-3 py-2 text-gray-900">
              <option value="new">Сначала новые</option>
              <option value="old">Сначала старые</option>
              <option value="alpha">По алфавиту</option>
            </select>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {items.map(r => (
              <article key={r.id} className="rounded-2xl p-6" style={{ background: 'rgba(255,255,255,.08)', backdropFilter: 'blur(8px)' }}>
                {badge(r.audience)}
                <h4 className="text-xl font-semibold mt-2">{r.title}</h4>
                <p className="text-sm text-gray-200 mt-1">{r.desc}</p>
                <div className="text-xs text-gray-300 mt-3">
                  {new Date(r.date).toLocaleDateString('ru-RU')} · {r.type} · {r.read} мин
                </div>
                <a href={r.href} className="mt-4 inline-block px-4 py-2 rounded-lg font-semibold" style={{ background: 'rgba(255,255,255,.1)' }}>Открыть</a>
              </article>
            ))}
          </div>
          {items.length === 0 && <p className="text-gray-300">По вашему запросу ничего не найдено. Очистите фильтры.</p>}
        </section>

        <section id="templates" className="space-y-4 scroll-mt-32">
          <h3 className="text-2xl font-bold">Готовые шаблоны</h3>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              { aud: 'employers', title: 'Шаблон вакансии (JD)', text: 'Обязанности, требования, KPI, компенсация.' },
              { aud: 'seekers', title: 'Шаблон резюме под ATS', text: 'Структура, ключевые слова, достижения.' },
              { aud: 'corporate', title: 'Шаблон SLA найма', text: 'Метрики, сроки, ответственность сторон.' },
            ].map((t) => (
              <a key={t.title} className="rounded-2xl p-6 hover:bg-white/10" style={{ background: 'rgba(255,255,255,.08)', backdropFilter: 'blur(8px)' }} href="#">
                {badge(t.aud as Audience)}
                <h4 className="text-xl font-semibold mt-2">{t.title}</h4>
                <p className="text-sm text-gray-200 mt-1">{t.text}</p>
              </a>
            ))}
          </div>
        </section>

        <section id="webinars" className="space-y-4 scroll-mt-32">
          <h3 className="text-2xl font-bold">Ближайшие вебинары</h3>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              ['employers', 'Как снизить CPH на 20% за квартал', 'Метрики, источники, RPO/подрядчики.'],
              ['seekers', 'Резюме, которое проходит ATS', 'Ключевые слова и фокус на impact.'],
              ['corporate', 'MSP/вендор-менеджмент: единые SLA', 'Как унифицировать ставки и отчётность.'],
            ].map(([a, t, d]) => (
              <article key={t} className="rounded-2xl p-6" style={{ background: 'rgba(255,255,255,.08)', backdropFilter: 'blur(8px)' }}>
                {badge(a as Audience)}
                <h4 className="text-xl font-semibold mt-2">{t}</h4>
                <p className="text-sm text-gray-200 mt-1">{d}</p>
                <a href="#subscribe" className="mt-4 inline-block px-4 py-2 rounded-lg font-semibold" style={{ background: 'rgba(255,255,255,.1)' }}>Получить запись</a>
              </article>
            ))}
          </div>
        </section>

        <section id="cases" className="space-y-4 scroll-mt-32">
          <h3 className="text-2xl font-bold">Кейсы</h3>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              ['employers', '48 наймов за 6 недель для склада', 'Явка 97%, SLA 24ч, −22% стоимости закрытия.'],
              ['corporate', 'MSP: экономия 9% на подрядчиках', 'Единая сетка ставок и контроль качества.'],
              ['seekers', 'Оффер +18% после подготовки', 'Мок-интервью и переговоры по офферу.'],
            ].map(([a, t, d]) => (
              <article key={t} className="rounded-2xl p-6" style={{ background: 'rgba(255,255,255,.08)', backdropFilter: 'blur(8px)' }}>
                {badge(a as Audience)}
                <h4 className="text-xl font-semibold mt-2">{t}</h4>
                <p className="text-sm text-gray-200 mt-1">{d}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="subscribe" className="space-y-4 scroll-mt-32">
          <h3 className="text-2xl font-bold">Рассылка «INTERIM. Практика найма»</h3>
          <p className="text-gray-200">Раз в неделю — полезные материалы и новые шаблоны. Отписка одним кликом.</p>
          <form
            onSubmit={handleSubscribeSubmit}
            className="rounded-2xl p-6 grid gap-4 md:grid-cols-3"
            style={{ background: 'rgba(255,255,255,.08)', backdropFilter: 'blur(8px)' }}
          >
            <input
              name="email"
              value={subscribeForm.email}
              onChange={handleSubscribeChange}
              className="rounded-xl px-4 py-3 text-gray-900"
              placeholder="Ваш e-mail"
              type="email"
              required
            />
            <input
              name="name"
              value={subscribeForm.name}
              onChange={handleSubscribeChange}
              className="rounded-xl px-4 py-3 text-gray-900"
              placeholder="Имя"
              required
            />
            <button
              type="submit"
              className="rounded-xl px-4 py-3 font-semibold"
              style={{ background: '#22c55e', color: '#052e16' }}
              disabled={isSubscribing}
            >
              {isSubscribing ? 'Подписка...' : 'Подписаться'}
            </button>
            {subscribeStatus === 'success' && (
              <div className="md:col-span-3 text-center text-sm text-green-300">
                ✅ Вы успешно подписаны на рассылку!
              </div>
            )}
            {subscribeStatus === 'error' && (
              <div className="md:col-span-3 text-center text-sm text-red-400">
                ❌ Ошибка при подписке. Пожалуйста, попробуйте снова.
              </div>
            )}
          </form>
          <p className="text-xs text-gray-400">Нажимая «Подписаться», вы соглашаетесь с обработкой персональных данных.</p>
        </section>

        <section id="submit" className="scroll-mt-32 space-y-4">
          <h3 className="text-2xl font-bold">Предложить тему материала</h3>
          <form
            onSubmit={handleSuggestionSubmit}
            className="rounded-2xl p-6 grid gap-4 md:grid-cols-2"
            style={{ background: 'rgba(255,255,255,.08)', backdropFilter: 'blur(8px)' }}
          >
            <input
              name="name"
              value={suggestionForm.name}
              onChange={handleSuggestionChange}
              className="rounded-xl px-4 py-3 text-gray-900"
              placeholder="Имя"
              required
            />
            <input
              name="email"
              value={suggestionForm.email}
              onChange={handleSuggestionChange}
              className="rounded-xl px-4 py-3 text-gray-900"
              placeholder="E-mail"
              type="email"
              required
            />
            <textarea
              name="message"
              value={suggestionForm.message}
              onChange={handleSuggestionChange}
              className="rounded-xl px-4 py-3 text-gray-900 md:col-span-2"
              rows={4}
              placeholder="Опишите тему/вопрос"
              required
            ></textarea>

            <div className="flex items-start md:col-span-2">
              <div className="flex items-center h-5">
                <input
                  type="checkbox"
                  id="suggestion-privacy-agreement"
                  name="privacy-agreement"
                  checked={isAgreed}
                  onChange={handleAgreementChange}
                  className="h-4 w-4 text-yellow-400 border-gray-300 rounded focus:ring-yellow-500"
                  style={{ color: '#FFC300' }}
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="suggestion-privacy-agreement" className="font-medium text-gray-300">
                  Я соглашаюсь с <a href="/privacy" className="underline hover:text-yellow-400" style={{ color: '#FFC300' }}>условиями обработки персональных данных</a>
                </label>
              </div>
            </div>

            {suggestionError && (
              <div className="md:col-span-2 text-center text-sm text-red-400 font-medium">
                ❌ **{suggestionError}**
              </div>
            )}

            <button
              type="submit"
              className="rounded-xl px-4 py-3 font-semibold md:col-span-2"
              style={{ background: '#FFC300', color: '#102a43' }}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Отправка...' : 'Отправить'}
            </button>
            {submitStatus === 'success' && (
              <div className="md:col-span-2 text-center text-sm text-green-300">
                ✅ Ваша заявка успешно отправлена!
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="md:col-span-2 text-center text-sm text-red-400">
                ❌ Ошибка при отправке. Пожалуйста, попробуйте снова.
              </div>
            )}
          </form>
        </section>
      </main>
    </div>
  );
}